import { gql, useLazyQuery } from "@apollo/client";
import { EndCustomer } from "@api/graphql/generated/generated-types";
import { useAuth } from "@src/common/hooks/useAuth";
import { useErrorToast } from "@src/common/hooks/useErrorToast";

const GET_END_CUSTOMER_BY_ID = gql`
  query GetEndCustomerById($courierId: ID!, $ids: [ID!]) {
    endCustomers(courierId: $courierId, ids: $ids, limit: 1) {
      edges {
        node {
          id
          courierId
          companyName
          contactPhoneNumber
          contactEmail
          firstName
          lastName
          displayId
          suite
          invoicingFrequency
          pricingListId
          visibleServiceTypeIds
          defaultServiceTypeId
          podEmails
          firebaseUid
          taxCode
          newCustomerConfirmationStatus
          endCustomerCreatedSource
          enabledCommunicationTypes
          totalPricingMultiplier
          billingEmails
          note
          scanRequirementType
          holidayScheduleId
          additionalRequiredFields
          dispatcherOrderEntryAdditionalFields
          preAuthorizeAmountMinimum
          preAuthorizeAmountMultiplier
          stopCompletionGuard
          invoiceCycleStartDate
          pickupPodRequirement
          dropoffPodRequirement
          returnPodRequirement
          pricingList {
            id
            displayId
            courierId
            name
            fallbackToBasePricingList
          }
          location {
            id
            streetAddress
            city
            state
            zipCode
            country
            lngLat {
              lng
              lat
            }
            googlePlaceId
          }
          emailTemplateGroup {
            id
            name
            isDefault
          }
          driverQuestionList {
            id
            courierId
            name
            isBase
            inAdditionToBase
          }
          costCenters {
            id
            endCustomerId
            name
            locationId
            suite
            email
            phone
            location {
              id
              streetAddress
              city
              state
              zipCode
              country
              lngLat {
                lng
                lat
              }
              googlePlaceId
            }
          }
        }
      }
    }
  }
`;

export function useEndCustomerById() {
  const { courierId } = useAuth();
  const showError = useErrorToast();

  const [query] = useLazyQuery(GET_END_CUSTOMER_BY_ID, {
    fetchPolicy: "no-cache",
    onError: (error) => showError(error),
  });

  const fetchEndCustomer = (
    endCustomerId: string
  ): Promise<EndCustomer | undefined> => {
    if (!courierId) return Promise.resolve(undefined);

    return query({
      variables: {
        courierId,
        ids: [endCustomerId],
      },
    }).then((res) => {
      return res.data?.endCustomers?.edges?.[0]?.node as
        | EndCustomer
        | undefined;
    });
  };

  return { fetchEndCustomer };
}
